<script setup lang="ts">
/* =============================================================
   VESSELS — LIST
   =============================================================
   Name, operating company and type. The dimensions are on the
   detail page: three numbers in metres are what you check about
   one ship, not what you scan a fleet by.

   Name leads, as on the berths list, because the name is what
   the row links to. The list itself is ordered by company and
   then name — see vesselComparator — so fleets read as fleets.

   Opening a vessel is the only thing a row does; edit and delete
   live on the detail page.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import FilterInput from '@/components/ui/FilterInput.vue'
import Icon from '@/components/Icon.vue'
import { routes, to } from '@/config/routes'
import { listVessels } from '@/lib/vessels'
import { useVesselTypes } from '@/lib/vesselTypes'
import { DataError } from '@/lib/errors'
import type { Vessel } from '@/lib/types'

const router = useRouter()
const { vesselTypeLabel } = useVesselTypes()

const rows = ref<Vessel[]>([])
const loading = ref(true)
const loadError = ref('')
const filter = ref('')

const visible = computed(() => {
  const q = filter.value.trim().toLowerCase()
  if (!q) return rows.value
  return rows.value.filter(
    (r) =>
      r.name.toLowerCase().includes(q) ||
      (r.company?.name ?? '').toLowerCase().includes(q) ||
      vesselTypeLabel(r.type).toLowerCase().includes(q),
  )
})

const countLabel = computed(() => {
  const total = rows.value.length
  const shown = visible.value.length
  const noun = total === 1 ? 'vessel' : 'vessels'
  return shown === total ? `${total} ${noun}` : `${shown} of ${total} ${noun}`
})

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    rows.value = await listVessels()
  } catch (e) {
    loadError.value = e instanceof DataError ? e.message : 'Could not load vessels.'
  } finally {
    loading.value = false
  }
}

onMounted(load)

/** See the note on the same function in BerthsPage. */
function openRow(vessel: Vessel, event: MouseEvent): void {
  if ((event.target as HTMLElement).closest('a')) return
  if (window.getSelection()?.toString()) return
  void router.push(to.vessel(vessel.id))
}
</script>

<template>
  <PageHeader
    title="Vessels"
    description="Registered ships, and the companies that operate them."
  >
    <template #actions>
      <Button variant="ghost" icon="refresh" :loading="loading" @click="load">Refresh</Button>
      <Button icon="plus" :to="routes.vesselNew">New vessel</Button>
    </template>
  </PageHeader>

  <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>

  <Panel flush :title="rows.length > 0 ? countLabel : undefined">
    <template v-if="rows.length > 0" #actions>
      <FilterInput
        v-model="filter"
        label="Filter vessels by name, company or type"
        placeholder="Filter by name, company or type"
      />
    </template>

    <p v-if="loading && rows.length === 0" class="panel-status">Loading…</p>

    <EmptyState
      v-else-if="rows.length === 0"
      icon="ship"
      title="No vessels yet"
      description="Nothing in the product registers a ship yet, so a pilot customer's fleet is entered here."
    >
      <template #action>
        <Button icon="plus" :to="routes.vesselNew">New vessel</Button>
      </template>
    </EmptyState>

    <p v-else-if="visible.length === 0" class="panel-status">No vessel matches “{{ filter }}”.</p>

    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Company</th>
            <th scope="col">Type</th>
            <th scope="col"><span class="sr-only">Open</span></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="vessel in visible"
            :key="vessel.id"
            class="rowLink"
            @click="openRow(vessel, $event)"
          >
            <td class="cell-strong">
              <RouterLink :to="to.vessel(vessel.id)" class="nameLink">{{ vessel.name }}</RouterLink>
            </td>
            <td class="cell-muted">
              <RouterLink v-if="vessel.company" :to="to.company(vessel.company.id)" class="nameLink">
                {{ vessel.company.name }}
              </RouterLink>
            </td>
            <td class="cell-muted">{{ vesselTypeLabel(vessel.type) }}</td>
            <td class="cell-actions">
              <Icon name="chevronRight" :size="16" class="chevron" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Panel>
</template>
